import type { HistoryOrderView, TaskView } from '@/services/manualTradingApi';

export type GroupExecutionNode = {
  taskId: string;
  task: TaskView | null;
  orders: HistoryOrderView[];
  updatedAtMs: number;
};

function sideRank(side: string): number {
  if (side === 'Long') return 0;
  if (side === 'Short') return 1;
  return 2;
}

export function buildGroupExecutionTree(
  tasks: TaskView[],
  orders: HistoryOrderView[],
): GroupExecutionNode[] {
  const taskById = new Map(tasks.map((task) => [task.task_id, task]));
  const ordersByTask = new Map<string, HistoryOrderView[]>();

  for (const order of orders) {
    const bucket = ordersByTask.get(order.task_id) ?? [];
    bucket.push(order);
    ordersByTask.set(order.task_id, bucket);
  }

  const taskIds = new Set<string>([
    ...tasks.map((task) => task.task_id),
    ...orders.map((order) => order.task_id),
  ]);

  const nodes: GroupExecutionNode[] = [];
  for (const taskId of taskIds) {
    const taskOrders = [...(ordersByTask.get(taskId) ?? [])].sort(
      (a, b) =>
        sideRank(a.position_side) - sideRank(b.position_side) ||
        a.updated_at_ms - b.updated_at_ms,
    );
    const task = taskById.get(taskId) ?? null;
    const updatedAtMs = Math.max(
      task?.updated_at ?? 0,
      ...taskOrders.map((order) => order.updated_at_ms),
    );
    nodes.push({ taskId, task, orders: taskOrders, updatedAtMs });
  }

  nodes.sort((a, b) => b.updatedAtMs - a.updatedAtMs);
  return nodes;
}

export function shortTaskId(taskId: string): string {
  if (taskId.length <= 14) return taskId;
  return `${taskId.slice(0, 8)}…${taskId.slice(-4)}`;
}
